import type {
  PrefillCreative,
  ProductContext,
} from "~/ui-rtk/app/slices/ad-forge.slice"
import { ImageCategory, type ScrapedImage } from "~/ui-rtk/pages/AdForge/types"

import {
  IMAGE_SETS,
  parseImageArray,
  type TProductDetail,
} from "./hooks/cube-query"

/**
 * Turn a mega_feed product row into an AdForge seed: a categorized asset pool
 * plus pre-filled ad copy.
 *
 * The feed's image sets already carry the classification AdForge wants — the
 * page-processing crawler sorted each product's images into static / lifestyle
 * / detail / model-studio buckets — so they map straight onto ImageCategory
 * instead of everything landing in OTHER. That categorization is what makes the
 * downstream image-to-video work useful: a lifestyle shot and a packshot want
 * very different motion treatments.
 */

const SET_CATEGORY: Record<string, ImageCategory> = {
  static_image_links: ImageCategory.LAYDOWN,
  lifestyle_image_links: ImageCategory.LIFESTYLE,
  detail_image_links: ImageCategory.PRODUCT_DETAIL,
  model_studio_image_links: ImageCategory.ON_BODY,
  additional_image_links: ImageCategory.OTHER,
  all_image_links: ImageCategory.OTHER,
}

const asText = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined
  const text = String(value).trim()
  return text.length > 0 ? text : undefined
}

/**
 * Builds the asset pool. Ordered so the most ad-usable imagery comes first
 * (lifestyle and on-model before packshots and misc), and de-duplicated by URL
 * because the feed deliberately repeats the same file across several sets —
 * `all_image_links` is a superset of the others, so it is processed last and
 * only contributes images no categorized set already claimed.
 */
export function toAdForgeAssets(product: TProductDetail): ScrapedImage[] {
  const ordered = [
    "lifestyle_image_links",
    "model_studio_image_links",
    "static_image_links",
    "detail_image_links",
    "additional_image_links",
    "all_image_links",
  ].filter(key => IMAGE_SETS.some(set => set.key === key))

  const seen = new Set<string>()
  const assets: ScrapedImage[] = []

  ordered.forEach(key => {
    parseImageArray(product[key]).forEach(url => {
      if (seen.has(url)) return
      seen.add(url)
      assets.push({
        // Stable per product+url so re-seeding the same product doesn't create
        // duplicate pool entries with fresh random ids.
        id: `mega-feed:${asText(product.item_group_id) ?? "unknown"}:${url}`,
        url,
        type: "image",
        category: SET_CATEGORY[key] ?? ImageCategory.OTHER,
        selected: false,
        sourceUrl: asText(product.link),
      })
    })
  })

  return assets
}

/**
 * Ad copy seed. header_description is the LLM-extracted one-liner and is the
 * best headline candidate; the long description becomes primary text. Nothing
 * is invented — every field is a verbatim feed value, so the user edits real
 * product copy rather than a placeholder.
 */
/**
 * Structured product facts for prompt injection. Kept as discrete fields
 * rather than a pre-joined blob so the consumer decides how much to spend on
 * prompt tokens — the compose prompt wants the whole thing, a motion prompt
 * only really needs the name, category and a one-line summary.
 */
export function toAdForgeProductContext(
  product: TProductDetail,
): ProductContext {
  return {
    productId: asText(product.item_group_id),
    name: asText(product.parent_title) ?? asText(product.title),
    headerDescription: asText(product.header_description),
    description:
      asText(product.description) ?? asText(product.product_detail_description),
    specs: asText(product.specs),
    brand: asText(product.brand),
    productType: asText(product.product_type),
    googleProductCategory: asText(product.google_product_category),
    gender: asText(product.gender),
    ageGroup: asText(product.age_group),
    price: asText(product.price),
    priceCurrency: asText(product.price_currency),
    tags: asText(product.tags),
    url: asText(product.link),
  }
}

export function toAdForgePrefill(product: TProductDetail): PrefillCreative {
  const title = asText(product.parent_title) ?? asText(product.title)

  return {
    adName: title,
    headline: asText(product.header_description) ?? title,
    description: asText(product.header_description),
    primaryText:
      asText(product.description) ?? asText(product.product_detail_description),
    destinationUrl: asText(product.link),
  }
}
